import { ArrowDownCircleIcon } from "@heroicons/react/24/outline";
import { JSX, useRef, useState, useEffect } from "react";
import video from "@/assets/animations/home.webm";
import poster from "@/assets/img/bg-home-lowres.webp";

const styles = {
  root: "relative isolate min-h-screen flex items-center justify-center mb-24",
  video:
    "absolute inset-0 w-full h-full object-cover z-0 pointer-events-none sm:object-center sm:h-full sm:w-full",
  overlay: "absolute inset-0 bg-black/30 z-10 pointer-events-none",
  content: "mx-auto max-w-4xl py-20 px-12 z-20",
  text: "text-center text-white",
  title:
    "relative z-10 text-3xl font-extrabold text-white sm:text-5xl lg:text-6xl",
  subtitle: "mt-6 text-lg text-white opacity-90 sm:text-xl",
  scrollContainer:
    "absolute bottom-20 flex justify-center w-full z-20 md:bottom-10",
  scrollIcon:
    "h-10 w-10 z-10 text-white hover:scale-110 hover:text-gray-200 transition duration-300 ease-in-out xl:h-20 w-20",
};

const HeroSection = (): JSX.Element => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    if (videoRef.current) {
      observer.observe(videoRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div className={styles.root}>
      <video
        ref={videoRef}
        className={styles.video}
        autoPlay
        poster={poster}
        loop
        muted
        playsInline
        preload={shouldLoad ? "auto" : "none"}
        onLoadedData={() => setVideoLoaded(true)}
        style={{
          opacity: videoLoaded ? 1 : 0,
          transition: "opacity 0.5s ease-in-out",
        }}
      >
        {shouldLoad && <source src={video} type="video/webm" />}
      </video>
      <div className={styles.overlay} />
      <div className={styles.content}>
        <div className={styles.text}>
          <h1 className={styles.title}>
            Recupera tu tiempo y profesionaliza la gestión de tu clínica
          </h1>
          <p className={styles.subtitle}>
            Elimina el caos administrativo. Organiza citas, historiales y
            recordatorios en una sola plataforma diseñada para veterinarios que
            buscan excelencia y tranquilidad.
          </p>
        </div>
      </div>
      <div className={styles.scrollContainer}>
        <a
          href="#problem"
          aria-label="More information"
          onClick={e => {
            e.preventDefault();
            const el = document.getElementById("problem");
            if (el) el.scrollIntoView({ behavior: "smooth" });
          }}
        >
          <ArrowDownCircleIcon
            className={styles.scrollIcon}
            aria-hidden="true"
          />
        </a>
      </div>
    </div>
  );
};

export default HeroSection;
